<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ !item.id ? $t('emailAccountCreateModalTitle') : $t('emailAccountEditModalTitle') }}</div>
    </template>

    <form class="p-fluid" @submit.prevent="saveItem" autocomplete="off">
      <Spinner v-if="dataIsSending || resumeIsLoading"/>

      <div v-else class="p-formgrid p-grid p-pt-3">
        <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
          <EmailInput v-model="accountData.username"
                      v-model:isInvalid="emailInvalid"
                      :submitted="submitted"
                      :required="true"
                      inputId="email-account-username"/>
        </div>

        <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <Password id="email-account-password"
                      :inputClass="submitted && changePassword && !accountData.password ? 'p-invalid' : ''"
                      v-model="accountData.password"
                      :disabled="!changePassword"
                      toggleMask
                      autocomplete="new-password"/>
            <label for="email-account-password">{{ $t('Password') }}<span class="warning-color" v-if="changePassword">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && changePassword && !accountData.password">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-pb-3"
             v-if="accountData.id"
             style="position: relative; top: -16px;">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="email-account-change-password" v-model="changePassword"/>
            </div>
            <label for="email-account-change-password" class="switch-label pointer">
              {{ $t('changePasswordLabel') }}
            </label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="email_account_sender_name"
                       v-model.trim="accountData.sender_name"
                       autocomplete="new-password"/>
            <label for="email_account_sender_name">{{ $t('Sender Name') }}</label>
          </div>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <CustomDropdown id="email_account_connection_type"
                            :class="{'p-invalid' : submitted && !selectedEncryptionType}"
                            v-model="selectedEncryptionType"
                            :options="encryptionTypes"
                            disabled
                            :filter="false"
                            optionLabel="value"
                            :showClear="false">
              <template #value="slotProps">
                <div v-if="slotProps.value">
                  <span>{{ slotProps.value.value }}</span>
                </div>
                <span v-else style="visibility: hidden">.</span>
              </template>
              <template #option="slotProps">
                <div>
                  <span>{{ slotProps.option.value }}</span>
                </div>
              </template>
            </CustomDropdown>
            <label for="email_account_connection_type">
              {{ $t('emailEncryptionLabel') }}<span class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && !selectedEncryptionType">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputText id="email_account_smtp_server"
                       :class="{'p-invalid' : submitted && !accountData.smtp_server}"
                       v-model.trim="accountData.smtp_server"
                       autocomplete="new-password"/>
            <label for="email_account_smtp_server">
              {{ $t('smtpServerSendingLabel') }}<span class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && !accountData.smtp_server">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5">
          <div class="p-float-label">
            <InputNumber :locale="computedNumberInputLocale"
                         id="email_account_smtp_port"
                         :format="false"
                         :inputClass="{'p-invalid' : submitted && !accountData.smtp_port}"
                         showButtons
                         :min="1"
                         :max="65535"
                         v-model="accountData.smtp_port"
                         autocomplete="new-password"/>
            <label for="email_account_smtp_port">SMTP Port<span class="warning-color">*</span></label>
          </div>
          <small class="p-invalid" v-if="submitted && !accountData.smtp_port">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5" v-if="!outgoingOnly">
          <div class="p-float-label">
            <InputText id="email_account_imap_server"
                       :class="{'p-invalid' : submitted && !accountData.imap_server && accountData.auto_receive_emails}"
                       v-model.trim="accountData.imap_server"
                       :disabled="!incomingMailAvailable"
                       autocomplete="new-password"/>
            <label for="email_account_imap_server">
              {{ $t('imapServerReceivingLabel') }}<span v-if="accountData.auto_receive_emails" class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && !accountData.imap_server && accountData.auto_receive_emails">{{ $t('Required field') }}</small>
        </div>

        <div class="p-field p-col-12 p-sm-12 p-md-6 p-lg-6 p-mb-5" v-if="!outgoingOnly">
          <div class="p-float-label">
            <InputNumber :locale="computedNumberInputLocale"
                         id="email_account_imap_port"
                         :format="false"
                         :inputClass="{'p-invalid' : submitted && !accountData.imap_port && accountData.auto_receive_emails}"
                         showButtons
                         :min="1"
                         :max="65535"
                         v-model="accountData.imap_port"
                         :disabled="!incomingMailAvailable"
                         autocomplete="new-password"/>
            <label for="email_account_imap_port">
              {{ $t('imapPortLabel') }}<span v-if="accountData.auto_receive_emails" class="warning-color">*</span>
            </label>
          </div>
          <small class="p-invalid" v-if="submitted && !accountData.imap_port && accountData.auto_receive_emails">{{ $t('Required field') }}</small>
        </div>

        <!--        <div v-if="!outgoingOnly && ($store.getters.tenantId === 2 || $store.getters.isSuperAdmin)"-->
        <div v-if="!outgoingOnly"
             class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-4 p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="auto_receive_emails"
                           :disabled="!incomingMailAvailable && !accountData.auto_receive_emails"
                           @change="toggleAutoReceiveEmails"
                           v-model="accountData.auto_receive_emails"/>
            </div>
            <label for="auto_receive_emails" class="switch-label pointer"
                   :class="{'no-pointer': !incomingMailAvailable && !accountData.auto_receive_emails}">
              {{ $t('autoReceiveEmailsLabel') }}
            </label>
            <!--            <button type="button" class="mail-setting-help cursor-help"-->
            <!--                    v-tooltip.top="$t('autoReceiveEmailsHint')"-->
            <!--                    :aria-label="$t('autoReceiveEmailsHint')">-->
            <!--              <i class="ti-help-alt" aria-hidden="true"/>-->
            <!--            </button>-->
            <i class="ti-help-alt cursor-help" aria-hidden="true" v-tooltip.top="$t('autoReceiveEmailsHint')"/>
          </div>
        </div>

        <div v-if="!outgoingOnly" class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-4 p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="email-account-mark-as-seen"
                           :disabled="!accountData.auto_receive_emails || !incomingMailAvailable"
                           v-model="accountData.imap_mark_as_seen"/>
            </div>
            <label for="email-account-mark-as-seen"
                   class="switch-label pointer"
                   :class="{'no-pointer': !accountData.auto_receive_emails || !incomingMailAvailable}">
              {{ $t('markEmailsAsSeenLabel') }}
            </label>
            <!--            <button type="button" class="mail-setting-help cursor-help"-->
            <!--                    v-tooltip.top="$t('markEmailsAsSeenHint')"-->
            <!--                    :aria-label="$t('markEmailsAsSeenHint')">-->
            <!--              <i class="ti-help-alt" aria-hidden="true"/>-->
            <!--            </button>-->
            <i class="ti-help-alt cursor-help" aria-hidden="true" v-tooltip.top="$t('markEmailsAsSeenHint')"/>
          </div>
        </div>

        <div v-if="!outgoingOnly" class="p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-4 p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <div class="p-d-flex p-ai-center">
              <InputSwitch id="email-account-unseen-only"
                           :disabled="!accountData.auto_receive_emails || !incomingMailAvailable"
                           v-model="accountData.imap_fetch_unseen_only"/>
            </div>
            <label for="email-account-unseen-only"
                   class="switch-label pointer"
                   :class="{'no-pointer': !accountData.auto_receive_emails || !incomingMailAvailable}">
              {{ $t('fetchUnseenEmailsOnlyLabel') }}
            </label>
            <!--            <button type="button" class="mail-setting-help cursor-help"-->
            <!--                    v-tooltip.top="$t('fetchUnseenEmailsOnlyHint')"-->
            <!--                    :aria-label="$t('fetchUnseenEmailsOnlyHint')">-->
            <!--              <i class="ti-help-alt" aria-hidden="true"/>-->
            <!--            </button>-->
            <i class="ti-help-alt cursor-help" aria-hidden="true" v-tooltip.top="$t('fetchUnseenEmailsOnlyHint')"/>
          </div>
        </div>

<!--        <div v-if="!outgoingOnly && accountData.id" class="p-col-12 p-mb-3">-->
<!--        <div v-if="!outgoingOnly &&-->
<!--                   accountData.id &&-->
<!--                   item &&-->
<!--                   [true, 1, '1'].includes(item.auto_receive_emails) &&-->
<!--                   accountData.auto_receive_emails"-->
<!--            class="p-col-12 p-mb-3">-->
        <div v-if="!outgoingOnly && accountData.id && accountData.auto_receive_emails" class="p-col-12 p-mb-3">
          <hr class="p-mt-2 p-mb-4">
          <Button type="button"
                  :label="$t('resumeMailReceivingLabel')"
                  icon="pi pi-refresh"
                  class="p-button-outlined p-button-sm p-button-secondary mail-resume-button"
                  :disabled="!canResumeReceiving"
                  :loading="resumeIsLoading"
                  @click="resumeConfirmVisible = true"/>
          <small class="p-d-block p-mt-2 p-text-secondary">{{ $t('resumeMailReceivingHint') }}</small>
          <small v-if="hasUnsavedReceivingSettings" class="p-d-block p-mt-2 p-text-secondary">
            {{ $t('resumeMailReceivingSaveFirst') }}
          </small>
        </div>

        <div v-if="!outgoingOnly && !incomingMailAvailable" class="p-col-12 p-mb-4">
          <BillingFeatureHint feature="incoming_mail"
                              :message-key="incomingMailPaused
                                  ? 'billing.features.incoming_mail.pausedHint'
                                  : 'billing.features.incoming_mail.availableHint'"
                              :show-change-plan="!canUseIncomingMail"
                              class="p-d-block"/>
        </div>

        <div v-if="incomingEmailExtraChargeRequired" class="p-col-12 p-mt-2 p-mb-3">
          <InlineNotice severity="warning"
                        icon="pi pi-info-circle"
                        :title="$t('billing.extraResources.additionalIncomingEmailTitle')">
            {{ $t('billing.extraResources.additionalIncomingEmailNotice', {
            included: incomingEmailResourceUsage.included_next,
            used: incomingEmailResourceUsage.used,
            price: incomingEmailResourceUsage.monthly_delta
          }) }}

            <template #actions>
              <div class="p-d-flex p-ai-center">
                <Checkbox id="incoming-email-extra-charge-confirmation"
                          v-model="billingExtraChargeConfirmed"
                          :binary="true"/>
                <label for="incoming-email-extra-charge-confirmation" class="pointer p-ml-2">
                  {{ $t('billing.extraResources.additionalIncomingEmailConfirmation', {
                  price: incomingEmailResourceUsage.monthly_delta
                }) }}
                </label>
              </div>
            </template>
          </InlineNotice>
        </div>

        <div v-if="emailAccountDuplicate && !dataIsSending && !resumeIsLoading" class="p-col-12 p-mt-3 p-mb-3">
          <InlineNotice severity="error" :compact="true">
            {{ $t('emailAccountAlreadyExists') }}
          </InlineNotice>
        </div>
      </div>
    </form>
    <template #footer>
      <Button :label="$t('Cancel')" :disabled="resumeIsLoading" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="resumeIsLoading || resumeConfirmVisible || disableSaveButton ||
                         billingResourceUsageIsLoading ||
                         (incomingEmailExtraChargeRequired && !billingExtraChargeConfirmed)"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
  <ConfirmModal :visible="resumeConfirmVisible"
                :text="$t('resumeMailReceivingConfirmation')"
                :modalDataIsLoading="resumeIsLoading"
                @close="closeResumeConfirmation"
                @confirm="resumeReceiving"/>
</template>


<script>
import httpClient from '@/services/http.services'
// import httpMixins from "@/mixins/httpMixins";
import formatMixins from '@/mixins/formatMixins';
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import inputsFormatMixins from "@/mixins/inputsFormatMixins";
import EmailInput from "@/components/EmailInput";
import billingFeaturesMixins from "@/mixins/billingFeaturesMixins";
import BillingFeatureHint from "@/components/billing/BillingFeatureHint.vue";
import InlineNotice from '@/components/InlineNotice.vue'
import ConfirmModal from '@/components/ConfirmModal.vue'

export default {
  mixins: [ showErrorsMixins, formatMixins, inputsFormatMixins, billingFeaturesMixins ],
  components: { EmailInput, BillingFeatureHint, InlineNotice, ConfirmModal },
  emits: ['close', 'update-items'],
  name: 'EmailAccountModal',
  props: {
    createInCurrentLocation: {
      type: Boolean,
      default: false
    },
    item: Object,
    visible: Boolean,
    apiLink: {
      type: String,
      default: 'email-account',
    },
    outgoingOnly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      resumeConfirmVisible: false,
      resumeIsLoading: false,
      emailInvalid: false,
      emailAccountDuplicate: false,
      changePassword: false,
      dataIsSending: false,
      disableSaveButton: false,
      accountData: {},
      submitted: false,
      selectedEncryptionType: null,
      encryptionTypes: [{value: 'TLS'}],
      billingResourceUsage: null,
      billingResourceUsageIsLoading: false,
      billingExtraChargeConfirmed: false,
    }
  },
  watch: {
    'accountData.username'() {
      this.emailAccountDuplicate = false
    },
    item(value) {
      this.emailAccountDuplicate = false
      if (!value) return false
      this.accountData = {
        ...value,
        auto_receive_emails: !!value.auto_receive_emails,
        imap_mark_as_seen: !!value.imap_mark_as_seen,
        imap_fetch_unseen_only: [true, 1, '1'].includes(value.imap_fetch_unseen_only),
      }

      this.changePassword = !value.id

      if (this.outgoingOnly) {
        this.accountData.imap_server = null
        this.accountData.imap_port = null
        this.accountData.auto_receive_emails = false
        this.accountData.imap_fetch_unseen_only = false
        this.accountData.imap_mark_as_seen = false
      } else if (!value.imap_port) {
        this.accountData.imap_port = 993
      }

      if (!value.smtp_port) {
        this.accountData.smtp_port = 587
      }

      if (value.encryption) {
        this.selectedEncryptionType = {
          value: value.encryption
        }
      } else {
        this.selectedEncryptionType = this.encryptionTypes[0]
      }
    },
    visible() {
      if (!this.visible) {
        this.resumeConfirmVisible = false
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.emailInvalid = false
        this.emailAccountDuplicate = false
        this.changePassword = false
        this.accountData = {}
        this.selectedEncryptionType = null
        this.billingResourceUsage = null
        this.billingResourceUsageIsLoading = false
        this.billingExtraChargeConfirmed = false
      } else if (!this.outgoingOnly) {
        this.loadBillingResourceUsage()
      }
    },
  },
  methods: {
    handleAccountValidationError(data) {
      if (data?.code !== 'EMAIL_ACCOUNT_EXISTS') return false
      this.emailAccountDuplicate = true
      return true
    },
    closeResumeConfirmation() {
      if (!this.resumeIsLoading) this.resumeConfirmVisible = false
    },
    async resumeReceiving() {
      if (!this.canResumeReceiving) return
      this.resumeIsLoading = true
      try {
        const { data } = await httpClient.post(`${this.apiLink}/resume-receiving`, {
          id: this.accountData.id,
          confirmed: true,
          generation: Number(this.accountData.imap_sync_generation ?? 0),
        })
        if (!data?.success) {
          this.showResumeError(data?.code)
          return
        }
        this.accountData.imap_last_uid = data.imap_last_uid
        this.accountData.imap_sync_generation = data.imap_sync_generation
        this.resumeConfirmVisible = false
        this.$toast.add({ severity: 'success', detail: this.$t('resumeMailReceivingSuccess'), life: settings.toastLife })
        this.$emit('update-items', this.accountData.id)
      } catch (err) {
        this.showResumeError(err?.response?.data?.code)
      } finally {
        this.resumeIsLoading = false
      }
    },
    showResumeError(code) {
      const keys = {
        MAIL_SYNC_BUSY: 'resumeMailReceivingBusy',
        MAIL_SYNC_CHANGED: 'resumeMailReceivingChanged',
        MAIL_SYNC_UNAVAILABLE: 'resumeMailReceivingUnavailable',
        MAIL_SYNC_CONNECTION_FAILED: 'resumeMailReceivingConnectionFailed',
      }
      this.resumeConfirmVisible = false
      this.$toast.add({ severity: 'error', detail: this.$t(keys[code] || 'resumeMailReceivingFailed'), life: settings.toastLife })
    },
    applyBillingResourceUsage(data) {
      this.billingResourceUsage = data?.success ? data : null
      this.billingExtraChargeConfirmed = false
    },
    async loadBillingResourceUsage() {
      if (this.outgoingOnly || this.billingResourceUsageIsLoading) return

      this.billingResourceUsageIsLoading = true

      try {
        const { status, data } = await httpClient.post('billing/resource-usage')

        if (status === 200 && data?.success) {
          this.applyBillingResourceUsage(data)
        } else {
          this.showUnknownError()
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.billingResourceUsageIsLoading = false
      }
    },
    handleExtraChargeConfirmationError(err) {
      const response = err?.response

      if (
          response?.status !== 409
          || response?.data?.code !== 'EXTRA_CHARGE_CONFIRMATION_REQUIRED'
      ) {
        return false
      }

      this.applyBillingResourceUsage(response.data.resource_usage)
      this.$toast.add({
        severity: 'warn',
        detail: this.$t('billing.extraResources.confirmationRequired'),
        life: settings.toastLife,
      })

      return true
    },
    toggleAutoReceiveEmails() {
      if (!this.accountData.auto_receive_emails) {
        this.accountData.imap_mark_as_seen = false
        this.billingExtraChargeConfirmed = false
      }
    },
    async saveItem() {
      if (this.resumeIsLoading || this.resumeConfirmVisible) return
      this.submitted = true
      if (this.emailInvalid
          || !this.accountData.username
          || (this.changePassword && !this.accountData.password)
          || !this.selectedEncryptionType
          || (
              !this.outgoingOnly
              && this.accountData.auto_receive_emails
              && !this.accountData.imap_server
          )
          || (
              !this.outgoingOnly
              && this.accountData.auto_receive_emails
              && !this.accountData.imap_port
          )
          || !this.accountData.smtp_server
          || !this.accountData.smtp_port
      ) {
        return false
      }

      if (this.incomingEmailExtraChargeRequired && !this.billingExtraChargeConfirmed) {
        return false
      }

      if (this.dataIsSending) return
      this.emailAccountDuplicate = false
      this.disableSaveButton = true
      this.dataIsSending = true

      const emailAccount = {
        id: this.accountData.id ?? null,
        ...this.accountData,

        imap_server: this.outgoingOnly
            ? null
            : (this.accountData.imap_server ?? null),

        imap_port: this.outgoingOnly
            ? null
            : (this.accountData.imap_port ?? null),

        auto_receive_emails: this.outgoingOnly
            ? false
            : !!this.accountData.auto_receive_emails,

        imap_mark_as_seen: this.outgoingOnly
            ? false
            : (
                this.accountData.auto_receive_emails
                && !!this.accountData.imap_mark_as_seen
            ),

        imap_fetch_unseen_only: this.outgoingOnly
            ? false
            : !!this.accountData.imap_fetch_unseen_only,

        password: this.changePassword
            ? this.accountData.password
            : null,

        username: this.accountData.username
            ? this.strToLowerCase(this.accountData.username)
            : null,

        encryption: this.selectedEncryptionType?.value ?? null,
      }


      if (this.incomingEmailExtraChargeRequired) {
        emailAccount.billing_confirmation = {
          confirmed: this.billingExtraChargeConfirmed,
          type: 'extra_incoming_email_account',
          monthly_delta: this.incomingEmailResourceUsage.monthly_delta,
          currency: this.billingResourceUsage.currency,
        }
      }

      if (!this.outgoingOnly && !this.accountData.id && this.createInCurrentLocation) {
        emailAccount.location_id = this.$store.getters.locationData?.id
      }

      if (this.accountData.id) {
        try {
          const { status, data } = await httpClient.post(`${this.apiLink}/update`, emailAccount)
          if (this.handleAccountValidationError(data)) return
          if (status === 200 && data?.success) {
            await this.$store.dispatch('reloadLocationBootstrapData')
            this.$emit('update-items', this.accountData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          if (!this.handleAccountValidationError(err?.response?.data)
              && !this.handleExtraChargeConfirmationError(err)) {
            this.showError(err)
          }
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`${this.apiLink}/create`, emailAccount)
          if (this.handleAccountValidationError(data)) return
          // console.log(data)
          if (status === 201 && data?.success) {
            this.accountData.id = data.id
            emailAccount.id = data.id
            await this.$store.dispatch('reloadLocationBootstrapData')
            this.$emit('update-items', this.accountData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife});
            this.close()
          } else if (data?.error) {
            if (settings.isDevMode) console.log(data.error)
            this.$toast.add({severity:'error', summary: data.error.summary, detail: data.error.detail, life: settings.toastLife});
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          if (!this.handleAccountValidationError(err?.response?.data)
              && !this.handleExtraChargeConfirmationError(err)) {
            this.showError(err)
          }
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      }
    },
    close() {
      if (this.resumeIsLoading) return
      this.resumeConfirmVisible = false
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    hasUnsavedReceivingSettings() {
      if (!this.accountData.id || !this.item) return false
      const textChanged = ['username', 'imap_server', 'imap_port'].some(
          key => String(this.accountData[key] ?? '') !== String(this.item[key] ?? '')
      )
      const flagsChanged = ['auto_receive_emails', 'imap_mark_as_seen', 'imap_fetch_unseen_only'].some(
          key => [true, 1, '1'].includes(this.accountData[key]) !== [true, 1, '1'].includes(this.item[key])
      )
      return textChanged || flagsChanged || this.changePassword
    },
    canResumeReceiving() {
      return !!this.accountData.id
          && !this.outgoingOnly
          && !!this.accountData.auto_receive_emails
          && this.incomingMailAvailable
          && !this.hasUnsavedReceivingSettings
          && !this.dataIsSending
          && !this.resumeIsLoading
    },
    incomingEmailResourceUsage() {
      return this.billingResourceUsage?.resources?.incoming_email_account ?? null
    },
    enablesIncomingEmail() {
      return (
          !this.outgoingOnly
          && !!this.accountData.auto_receive_emails
          && (!this.item?.id || !this.item?.auto_receive_emails)
      )
    },
    incomingEmailExtraChargeRequired() {
      return (
          this.enablesIncomingEmail
          && !!this.incomingEmailResourceUsage?.requires_confirmation
      )
    },
    incomingMailAvailable() {
      if (this.accountData?.id) {
        return !!this.item?.incomingMailAvailable
      }

      return this.createInCurrentLocation && this.canUseIncomingMail
    },

    incomingMailPaused() {
      return (
          !!this.accountData?.id
          && !!this.accountData.auto_receive_emails
          && !this.incomingMailAvailable
      )
    },

    computedWindowSize() {
      if (this.$store.state.mobileLayout) {
        return {position: 'absolute', top: 0, width: '95%', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '550px', marginTop: '15px'}
      } else {
        return {position: 'absolute', top: 0, width: '550px'}
      }
    },
  },

}
</script>

<style scoped lang="scss">
//.mail-resume-section {
//  padding-top: 0.75rem;
//  border-top: 1px solid var(--surface-border, #dee2e6);
//}

.mail-resume-button {
  width: auto !important;
}

//.mail-setting-help {
//  display: inline-flex;
//  align-items: center;
//  justify-content: center;
//  flex-shrink: 0;
//  //width: 1.5rem;
//  //height: 1.5rem;
//  //margin-left: 2px;
//  padding: 0;
//  border: 0;
//  border-radius: 50%;
//  background: transparent;
//  color: inherit;
//}
//
//.mail-setting-help:focus-visible {
//  outline: 2px solid currentColor;
//  outline-offset: 2px;
//}

.table-wrapper {
  overflow-y: auto;
}

.users-table {
  border-collapse: collapse;
  width: 100%;
  .users-table__name {
    //width: 90%;
    //width: auto;
  }
  .users-table__buttons {
    //width: 100%;
  }
}

.p-dialog-footer {
  margin-top: -20px!important;
}

</style>